import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Plus, Loader2, MessageSquare } from "lucide-react";
import { ACTIVITY_TYPES, INPUT_CLS, activityType, formatDate } from "./leadDetailModel";

const TypePicker = ({ value, onChange }) => (
  <div className="flex gap-2">
    {ACTIVITY_TYPES.map((type) => {
      const Icon = type.icon;
      return (
        <button key={type.value} onClick={() => onChange(type.value)} title={type.label} data-testid={`activity-type-${type.value}`}
          className={`p-2 rounded-lg border transition-colors ${value === type.value ? "bg-[#00A38A]/20 border-[#00A38A]" : "border-gray-700 hover:border-gray-500"}`}>
          <Icon className={`h-4 w-4 ${type.color}`} />
        </button>
      );
    })}
  </div>
);

export const ActivityForm = ({ activity, setField, saving, onSave, onCancel }) => (
  <div className="bg-white/5 border border-gray-700 rounded-lg p-4 mb-4" data-testid="activity-form">
    <div className="grid grid-cols-2 gap-3 mb-3">
      <div>
        <label className="text-xs text-gray-500 mb-1 block">Type</label>
        <TypePicker value={activity.type} onChange={(v) => setField("type", v)} />
      </div>
      <div>
        <label className="text-xs text-gray-500 mb-1 block">Subject</label>
        <Input value={activity.subject} onChange={(e) => setField("subject", e.target.value)} placeholder="Brief subject..." className={INPUT_CLS} data-testid="activity-subject-input" />
      </div>
    </div>
    <div className="mb-3">
      <label className="text-xs text-gray-500 mb-1 block">Description</label>
      <Textarea value={activity.description} onChange={(e) => setField("description", e.target.value)} placeholder="What happened?" className={`${INPUT_CLS} min-h-[60px]`} />
    </div>
    <div className="mb-3">
      <label className="text-xs text-gray-500 mb-1 block">Outcome (optional)</label>
      <Input value={activity.outcome} onChange={(e) => setField("outcome", e.target.value)} placeholder="Result or next steps..." className={INPUT_CLS} />
    </div>
    <div className="flex gap-2">
      <Button onClick={onSave} disabled={saving} className="bg-[#00A38A] hover:bg-[#008570] text-white" data-testid="activity-save-btn">
        {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Save Activity
      </Button>
      <Button variant="outline" onClick={onCancel} className="border-gray-700 text-gray-300">Cancel</Button>
    </div>
  </div>
);

const ActivityItem = ({ activity }) => {
  const type = activityType(activity.type);
  const Icon = type.icon;
  return (
    <div className="flex gap-3">
      <div className={`w-8 h-8 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 ${type.color}`}><Icon className="h-4 w-4" /></div>
      <div className="flex-1 bg-white/5 border border-gray-800 rounded-lg p-3">
        <div className="flex items-center justify-between mb-1">
          <span className="text-white font-medium text-sm">{activity.subject}</span>
          <span className="text-xs text-gray-500">{formatDate(activity.createdAt)}</span>
        </div>
        {activity.description && <p className="text-gray-400 text-sm">{activity.description}</p>}
        {activity.outcome && <p className="text-[#00A38A] text-xs mt-2">Outcome: {activity.outcome}</p>}
      </div>
    </div>
  );
};

export const ActivityList = ({ activities, loading }) => (
  <ScrollArea className="flex-1">
    {loading ? (
      <div className="flex items-center justify-center h-40"><Loader2 className="h-8 w-8 animate-spin text-[#00A38A]" /></div>
    ) : activities.length > 0 ? (
      <div className="space-y-4" data-testid="activity-list">
        {activities.map((a, idx) => <ActivityItem key={a.activityId || `${a.createdAt}-${idx}`} activity={a} />)}
      </div>
    ) : (
      <div className="text-center py-12" data-testid="activity-empty">
        <MessageSquare className="h-12 w-12 text-gray-700 mx-auto mb-3" />
        <p className="text-gray-500">No activities logged yet</p>
        <p className="text-gray-600 text-sm mt-1">Click &quot;Log Activity&quot; to add the first one</p>
      </div>
    )}
  </ScrollArea>
);

export const ActivityTimeline = ({ d }) => (
  <div className="flex-1 p-6 flex flex-col">
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-sm font-semibold text-gray-400 tracking-wide">Activity Timeline</h3>
      <Button size="sm" onClick={() => d.setShowAddActivity(!d.showAddActivity)} className="bg-[#00A38A] hover:bg-[#008570] text-white" data-testid="log-activity-btn">
        <Plus className="h-4 w-4 mr-1" /> Log Activity
      </Button>
    </div>
    {d.showAddActivity && <ActivityForm activity={d.newActivity} setField={d.setActivityField} saving={d.addingActivity} onSave={d.handleAddActivity} onCancel={() => d.setShowAddActivity(false)} />}
    <ActivityList activities={d.activities} loading={d.loading} />
  </div>
);
